/* eslint-disable react/prop-types */
import { get } from "lodash";
import React, { Component, createContext } from "react";
import { connect } from "react-redux";
import { getFirebaseUser, updateFirebaseUser } from "../helper/api";
import { BottomPlayer } from "./bottom-player";
import { PodcastPlayer } from "./podcast-player";
import { Queue } from "./queue";
import { ShowNotes } from "./show-notes";
import { setGlobalPlayerRef } from "./utils";

const initialState = {
  queue: [],
  playbackRate: 1,
  currentTrackId: null,
  audioStatus: null,
  currentTime: 0,
  currentScreen: null,
  showPlayer: false,
  loadingUserQueue: false,
  showPlayerFullScreen: false,
  showNotes: false
};

const PlayerContext = createContext(initialState);

let PlayerContextProvider = class PlayerContextProvider extends Component {
  constructor(props) {
    super(props);
    setGlobalPlayerRef(this);
    this.state = initialState;
    this.listeners = [];
  }

  canPlayHandler = () => {
    // Refer - https://stackoverflow.com/questions/39437369/html5-audio-changing-currenttime-fires-multiple-canplay-events
    console.log("can play handler ");
    if (this.initialLoad) {
      console.log("initial load ", this.state.currentTime);
      if (this.state.currentTime > 0) {
        this.audio.currentTime = this.state.currentTime;
      }

      this.initialLoad = false;
    }

    if (this.audio.paused) {
      this.setState({ audioStatus: "paused" });
    } else {
      this.setState({ audioStatus: "playing" });
    }
  };

  attachListeners() {
    const audio = new window.Audio();
    this.audio = audio;
    window.audio = audio;
    this.audio.onpause = () => {
      this.setState({ audioStatus: "paused" });
    };

    this.audio.onplay = () => {
      this.setState({ audioStatus: "playing" });
    };

    this.audio.onplaying = () => {
      this.setState({ audioStatus: "playing" });
    };

    this.audio.onratechange = () => {
      this.setState({ playbackRate: audio.playbackRate }, this.syncUserState);
    };

    this.audio.oncanplaythrough = () => {
      console.log("canplaythrough");
      this.canPlayHandler();
    };

    this.audio.oncanplay = () => {
      console.log("canplay");
      this.canPlayHandler();
    };

    this.savingPlayerState = false;

    this.audio.ontimeupdate = () => {
      this.setState({ currentTime: audio.currentTime });

      if (!this.savingPlayerState) {
        this.savingPlayerState = true;
        setTimeout(() => {
          this.syncUserState();
          this.savingPlayerState = false;
        }, 10000);
      }
    };

    this.audio.onended = () => {
      if (this.state.queue.length > 1) {
        this.playNextFromQueue();
      }
    };

    this.audio.onloadstart = () => {
      console.log("loadstart");
      this.setState({ audioStatus: "loading" });
    };

    this.audio.onseeking = () => {
      console.log("seeking");
      this.setState({ audioStatus: "loading" });
    };

    this.audio.onseeked = () => {
      console.log("onseeked");

      if (audio.paused) {
        this.setState({ audioStatus: "paused" });
      } else {
        this.setState({ audioStatus: "playing" });
      }
    };
  }

  restorePersistedState = async () => {
    this.setState({
      loadingUserQueue: true
    });

    try {
      const { data } = await getFirebaseUser();

      if (data && data.playerState) {
        const playerState = data.playerState;
        const track = playerState.queue.find(item => item._id === playerState.currentTrackId);
        if (track) {
          this.setState(
            {
              ...this.state,
              ...playerState,
              currentScreen: null
            },
            () => {
              this.initialLoad = true;
              this.userStateRestored = true;
              this.audio.src = track.streamUrl;
              this.audio.title = track.title;
              this.audio.defaultPlaybackRate = playerState.playbackRate;
              this.audio.playbackRate = playerState.playbackRate;
            }
          );
        } else {
          this.userStateRestored = true;
        }
      }
      // Respect user preference above or else fallback to default behaviour
      else if (this.userClickedMicIcon) {
        this.togglePlayer();
        this.userStateRestored = true;
        this.userClickedMicIcon = false;
      } else {
        this.userStateRestored = true;
      }
    } catch (e) {
      console.log("Error while loading user player state ", e);
    } finally {
      this.setState({
        loadingUserQueue: false
      });
    }
  };

  syncUserState = () => {
    if (this.props.member && this.userStateRestored) {
      const { queue, playbackRate, currentTrackId, currentTime, showPlayer } = this.state;

      const stateToPersist = {
        id: this.props.member.id,
        playerState: {
          queue,
          playbackRate,
          currentTrackId,
          currentTime,
          showPlayer
        }
      };

      updateFirebaseUser(stateToPersist);
    }
  };

  componentDidMount() {
    this.attachListeners();
  }

  componentDidUpdate(prevProps, prevState) {
    this.listeners.forEach(l => l(this.state));

    // User logged in
    if (this.props.member !== prevProps.member && this.props.member) {
      this.reset();
      this.attachListeners();
      this.restorePersistedState();
    }

    // User logged out
    if (prevProps.member && !this.props.member) {
      this.reset();
    }

    if (this.state.queue !== prevState.queue) {
      this.syncUserState();
    }

    if (this.state.showPlayer !== prevState.showPlayer) {
      this.syncUserState();
    }
  }

  addPlayerListener = cb => {
    this.listeners.push(cb);

    cb(this.state);

    return () => {
      this.listeners.filter(l => l !== cb);
    };
  };

  playNextFromQueue = () => {
    const nextQueue = this.state.queue.filter(item => item._id !== this.state.currentTrackId);
    if (nextQueue[0]) {
      this.setTrack(nextQueue[0]);
      this.setState({
        queue: nextQueue
      });
      this.play();
    }
  };

  playTrack = (track, cb) => {
    let nextQueue = this.state.queue.filter(item => item._id !== track._id);
    nextQueue = [track, ...nextQueue];
    this.setState(
      {
        queue: nextQueue
      },
      cb
    );
    this.setTrack(track);
    this.play();
  };

  setTrack = track => {
    this.audio.src = track.streamUrl;
    this.audio.title = track.title;

    this.setState({
      currentTrackId: track._id,
      currentTime: 0
    });
  };

  hidePlayer = () => {
    this.setState({ showPlayer: false });
  };

  togglePlayer = () => {
    // Pause the audio if user is hiding the player
    if (this.state.showPlayer) {
      this.audio.pause();
    }

    // If a user opens the player but the track queue is empty
    if (!this.state.showPlayer && !this.state.currentTrackId) {
      this.setState({
        currentScreen: "library",
        showPlayer: true
      });
    } else {
      this.setState({
        showPlayer: !this.state.showPlayer
      });
    }
  };

  addTrackToQueue = track => {
    this.setState({
      queue: [...this.state.queue, track]
    });
  };

  removeTrackFromQueue = trackId => {
    const nextQueue = this.state.queue.filter(item => item._id !== trackId);
    this.setState({
      queue: nextQueue
    });
  };

  moveUpQueue = track => {
    const trackIndex = this.state.queue.indexOf(track);
    console.log(trackIndex);
    if (trackIndex > 0) {
      const newQueue = this.state.queue;
      newQueue.splice(trackIndex - 1, 0, this.state.queue[trackIndex]);
      newQueue.splice(trackIndex + 1, 1);
      this.setState({
        ...this.state,
        queue: newQueue
      });
    }
  };

  moveDownQueue = track => {
    const trackIndex = this.state.queue.indexOf(track);
    console.log(trackIndex);
    if (trackIndex < this.state.queue.length && trackIndex !== this.state.queue.length - 1) {
      const newQueue = this.state.queue;
      newQueue.splice(trackIndex, 0, this.state.queue[trackIndex + 1]);
      newQueue.splice(trackIndex + 2, 1);
      this.setState({
        ...this.state,
        queue: newQueue
      });
    }
  };

  isTrackPresentInQueue = trackId => {
    const track = this.state.queue.find(item => item._id === trackId);
    if (track) {
      return true;
    }

    return false;
  };

  reset = () => {
    this.audio.pause();
    this.userStateRestored = false;
    // this.setState({ showPlayer: false });
  };

  setCurrentScreen = name => {
    this.setState({ currentScreen: name });
  };

  getCurrentTrack = () => {
    const track = this.state.queue.find(track => track._id === this.state.currentTrackId);
    return track;
  };

  play = () => {
    this.audio.play();
    if (!this.state.showPlayer) {
      this.setState({ showPlayer: true });
    }
  };

  changePlaybackSpeed = rate => {
    this.audio.playbackRate = rate;
    this.audio.defaultPlaybackRate = rate;
  };

  seek = time => {
    this.setState({
      currentTime: time
    });
    this.audio.currentTime = time;
  };

  pause = () => {
    this.audio.pause();
  };

  toggle = () => {
    if (this.state.audioStatus === "playing") {
      this.audio.pause();
    } else if (this.state.audioStatus === "paused") {
      this.audio.play();
    }
  };

  setShowPlayerFullScreen = value => {
    this.setState({
      showPlayerFullScreen: value
    });
  };

  toggleShowNotes = () => {
    this.setState({
      showNotes: !this.state.showNotes
    });
  };

  render() {
    const currentTrack = this.getCurrentTrack();
    // console.log("Player state", this.state);

    return (
      <PlayerContext.Provider
        value={{
          ...this.state,
          audio: this.audio,
          playTrack: this.playTrack,
          play: this.play,
          pause: this.pause,
          reset: this.reset,
          getCurrentTrack: this.getCurrentTrack,
          seek: this.seek,
          isTrackPresentInQueue: this.isTrackPresentInQueue,
          togglePlayer: this.togglePlayer,
          hidePlayer: this.hidePlayer,
          addTrackToQueue: this.addTrackToQueue,
          removeTrackFromQueue: this.removeTrackFromQueue,
          moveUpQueue: this.moveUpQueue,
          moveDownQueue: this.moveDownQueue,
          setCurrentScreen: this.setCurrentScreen,
          changePlaybackSpeed: this.changePlaybackSpeed,
          setShowPlayerFullScreen: this.setShowPlayerFullScreen,
          toggleShowNotes: this.toggleShowNotes
        }}
      >
        {this.state.showPlayer && <BottomPlayer />}
        <PodcastPlayer handleClose={() => this.setCurrentScreen(null)} currentScreen={this.state.currentScreen} />
        <Queue handleClose={() => this.setCurrentScreen(null)} currentScreen={this.state.currentScreen} />
        {currentTrack ? <ShowNotes currentTrack={currentTrack} /> : null}
      </PlayerContext.Provider>
    );
  }
};

function mapStateToProps(state) {
  return {
    member: get(state, ["member"], null),
    userHasSubscription: get(state, ["isSubscribedUser", "userHasSubscription"], false)
  };
}

PlayerContextProvider = connect(mapStateToProps, null)(PlayerContextProvider);

export { PlayerContextProvider, PlayerContext };
